// OWNER: Agent C. In-memory mock backend: mutable store + the same valuation
// rules described in PLAN.md §3 and the same mutation table in §2, so the
// MSW-backed UI behaves like the real Worker. When the real endpoints land,
// deleting this directory and flipping off MSW is the only change needed.
import type { z } from "zod";
import type {
  Account,
  AccountValuation,
  Country,
  CountryValuation,
  Currency,
  Holding,
  HoldingValuation,
  Loan,
  LoanPayment,
  LoanValuation,
  PortfolioSummary,
  SnapshotPoint,
  Transaction,
  TransactionType,
  User,
} from "@shared/types";
import type {
  createAccountSchema,
  createHoldingSchema,
  createLoanSchema,
  createUserSchema,
  updateAccountSchema,
  updateHoldingSchema,
} from "@shared/schemas";
import { safeGainPct, formatRelativeAge } from "@shared/format";
import {
  ACCOUNTS,
  HOLDINGS,
  LOANS,
  LOAN_PAYMENTS,
  TRANSACTIONS,
  USERS,
  FRESH_PRICES,
  FX_FRESH,
  STALE_SYMBOL,
  type PriceEntry,
  type Scenario,
} from "./fixtures";

export type { Scenario };

const SCENARIO_KEY = "gringotts:mockScenario";

export function getScenario(): Scenario {
  if (typeof localStorage === "undefined") return "default";
  const v = localStorage.getItem(SCENARIO_KEY);
  return v === "stale" || v === "empty" ? v : "default";
}

export function setScenario(scenario: Scenario) {
  localStorage.setItem(SCENARIO_KEY, scenario);
}

const ROLE_KEY = "gringotts:mockRole";

/** Dev-only "view as" switch (Settings page) so admin-gating is checkable
 * without waiting on Agent A's real login flow. */
export function getMockRole(): User["role"] {
  if (typeof localStorage === "undefined") return "admin";
  return localStorage.getItem(ROLE_KEY) === "member" ? "member" : "admin";
}

export function setMockRole(role: User["role"]) {
  localStorage.setItem(ROLE_KEY, role);
}

export function currentUser(): User {
  const role = getMockRole();
  const users = db().users;
  return users.find((u) => u.role === role) ?? users[0]!;
}

// ── mutable store ───────────────────────────────────────────────────────

interface Store {
  accounts: Account[];
  holdings: Holding[];
  loans: Loan[];
  loanPayments: LoanPayment[];
  transactions: Transaction[];
  users: User[];
  nextTxnId: number;
  nextPaymentId: number;
  nextAccountId: number;
  nextHoldingId: number;
  nextLoanId: number;
  nextUserId: number;
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}

function buildStore(scenario: Scenario): Store {
  if (scenario === "empty") {
    return {
      accounts: clone(ACCOUNTS),
      holdings: [],
      loans: [],
      loanPayments: [],
      transactions: [],
      users: clone(USERS),
      nextTxnId: 9000,
      nextPaymentId: 9000,
      nextAccountId: 9000,
      nextHoldingId: 9000,
      nextLoanId: 9000,
      nextUserId: 9000,
    };
  }
  return {
    accounts: clone(ACCOUNTS),
    holdings: clone(HOLDINGS),
    loans: clone(LOANS),
    loanPayments: clone(LOAN_PAYMENTS),
    transactions: clone(TRANSACTIONS),
    users: clone(USERS),
    nextTxnId: 2000,
    nextPaymentId: 100,
    nextAccountId: 100,
    nextHoldingId: 1000,
    nextLoanId: 1000,
    nextUserId: 100,
  };
}

let store = buildStore(getScenario());
let storeScenario = getScenario();

/** Re-reads localStorage; call at the top of every handler so a scenario
 * switch (Settings page) takes effect without a full reload of this module. */
export function db(): Store {
  const current = getScenario();
  if (current !== storeScenario) {
    store = buildStore(current);
    storeScenario = current;
  }
  return store;
}

export function resetStore() {
  store = buildStore(storeScenario);
}

// ── prices & fx ──────────────────────────────────────────────────────────

function priceFor(symbol: string): PriceEntry | undefined {
  return FRESH_PRICES[symbol];
}

function isStaleScenario() {
  return getScenario() === "stale";
}

function priceAsOf(symbol: string, ageMs: number): string {
  const stale = isStaleScenario() && symbol === STALE_SYMBOL ? 50 * 60 * 60 * 1000 /* 50h */ : ageMs;
  return new Date(Date.now() - stale).toISOString();
}

function priceIsStale(symbol: string): boolean {
  return isStaleScenario() && symbol === STALE_SYMBOL;
}

export function fxRate(from: Currency): number {
  if (from === "INR") return 1;
  if (from === "GBP") return FX_FRESH.GBPINR;
  return FX_FRESH.USDINR;
}

export function fxSnapshot() {
  const stale = isStaleScenario();
  return {
    GBPINR: FX_FRESH.GBPINR,
    USDINR: FX_FRESH.USDINR,
    asOf: new Date(Date.now() - (stale ? 50 * HOUR() : 30 * 60 * 1000)).toISOString(),
    isStale: false,
  };
}
function HOUR() {
  return 60 * 60 * 1000;
}

// ── valuation ────────────────────────────────────────────────────────────

function holdingNativeValue(h: Holding): { value: number; price: number | null } {
  if (h.valuationMode === "CASH_BALANCE") return { value: h.cashBalance, price: null };
  if (h.valuationMode === "MANUAL_TOTAL") return { value: h.manualValue ?? 0, price: h.manualPrice };
  // UNITS_X_PRICE
  const entry = h.symbol ? priceFor(h.symbol) : undefined;
  const price = h.manualPrice ?? entry?.price ?? null;
  return { value: (price ?? 0) * h.units, price };
}

function valueHolding(h: Holding): HoldingValuation {
  const entry = h.symbol ? priceFor(h.symbol) : undefined;
  const { value, price } = holdingNativeValue(h);
  const isStale = h.valuationMode === "UNITS_X_PRICE" && !!h.symbol && priceIsStale(h.symbol);
  const gainNative = value - h.investedAmount;
  return {
    id: h.id,
    name: h.name,
    symbol: h.symbol,
    units: h.units,
    price,
    priceCurrency: h.currency,
    priceAsOf: entry && h.symbol ? priceAsOf(h.symbol, entry.ageMs) : h.valuationMode === "CASH_BALANCE" ? new Date().toISOString() : null,
    priceSource: h.manualPrice != null ? "manual" : (entry?.source ?? null),
    isStale,
    valueNative: value,
    valueInr: value * fxRate(h.currency),
    investedNative: h.investedAmount,
    gainNative,
    gainPct: safeGainPct(gainNative, h.investedAmount),
    lastTransactionAt: h.lastTransactionAt,
  };
}

function valueAccount(a: Account, holdings: Holding[]): AccountValuation {
  const valuations = holdings.filter((h) => h.accountId === a.id && h.isActive).map(valueHolding);
  const valueNative = valuations.reduce((s, h) => s + h.valueNative, 0);
  const investedNative = valuations.reduce((s, h) => s + h.investedNative, 0);
  const gainNative = valueNative - investedNative;
  return {
    id: a.id,
    name: a.name,
    kind: a.kind,
    currency: a.currency,
    valueNative,
    valueInr: valueNative * fxRate(a.currency),
    investedNative,
    gainNative,
    gainPct: safeGainPct(gainNative, investedNative),
    holdings: valuations,
  };
}

function valueCountry(country: Country, accounts: Account[], holdings: Holding[]): CountryValuation {
  const accs = accounts.filter((a) => a.country === country && a.isActive).map((a) => valueAccount(a, holdings));
  const valueInr = accs.reduce((s, a) => s + a.valueInr, 0);
  const isUk = country === "UK";
  return {
    country,
    valueInr,
    valueNative: isUk ? accs.reduce((s, a) => s + a.valueNative, 0) : null,
    nativeCurrency: isUk ? "GBP" : null,
    accounts: accs,
  };
}

function valueLoan(l: Loan): LoanValuation {
  const paidPct = l.principal > 0 ? ((l.principal - l.outstandingPrincipal) / l.principal) * 100 : 0;
  return {
    id: l.id,
    name: l.name,
    currency: l.currency,
    outstanding: l.outstandingPrincipal,
    outstandingInr: l.outstandingPrincipal * fxRate(l.currency),
    emiAmount: l.emiAmount,
    nextDueDate: l.nextDueDate,
    interestRate: l.interestRate,
    paidPct,
  };
}

export function computeSummary(): PortfolioSummary {
  const s = db();
  const countries: CountryValuation[] = (["IN", "UK"] as Country[]).map((c) => valueCountry(c, s.accounts, s.holdings));
  const investmentsInr = countries.reduce((sum, c) => sum + c.valueInr, 0);
  const investedCostInr = s.holdings
    .filter((h) => h.isActive)
    .reduce((sum, h) => sum + h.investedAmount * fxRate(h.currency), 0);
  const unrealisedGainInr = investmentsInr - investedCostInr;
  const loans = s.loans.filter((l) => l.status === "active").map(valueLoan);
  const loansOutstandingInr = loans.reduce((sum, l) => sum + l.outstandingInr, 0);

  const warnings: string[] = [];
  for (const country of countries) {
    for (const acc of country.accounts) {
      for (const h of acc.holdings) {
        if (h.isStale && h.priceAsOf) {
          warnings.push(`Stale price for ${h.symbol ?? h.name} (${formatRelativeAge(h.priceAsOf)})`);
        }
      }
    }
  }

  return {
    asOf: new Date().toISOString(),
    baseCurrency: "INR",
    fx: fxSnapshot(),
    netWorthInr: investmentsInr - loansOutstandingInr,
    totals: {
      investmentsInr,
      loansOutstandingInr,
      investedCostInr,
      unrealisedGainInr,
      unrealisedGainPct: safeGainPct(unrealisedGainInr, investedCostInr),
    },
    countries,
    loans,
    warnings,
  };
}

export function computeHistory(range: "1M" | "6M" | "1Y" | "ALL"): SnapshotPoint[] {
  const summary = computeSummary();
  const points = 24; // ~24 months of visit-driven snapshots
  const out: SnapshotPoint[] = [];
  const today = new Date();
  for (let i = points; i >= 0; i--) {
    const d = new Date(today);
    d.setMonth(d.getMonth() - i);
    const growth = 1 - i * 0.018 + Math.sin(i / 3) * 0.01;
    out.push({
      date: d.toISOString().slice(0, 10),
      totalInvestmentsInr: Math.round(summary.totals.investmentsInr * Math.max(growth, 0.05)),
      totalLoansInr: Math.round(summary.totals.loansOutstandingInr * Math.min(1 + i * 0.01, 1.4)),
      netWorthInr: 0,
    });
  }
  for (const p of out) p.netWorthInr = p.totalInvestmentsInr - p.totalLoansInr;

  const cutoff = { "1M": 1, "6M": 6, "1Y": 12, ALL: points + 1 }[range];
  return out.slice(Math.max(0, out.length - cutoff - 1));
}

// ── transaction mutation (mirrors PLAN.md §2's mutation table) ─────────

export class MockApiError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
  }
}

export function applyTransaction(
  holdingId: number,
  type: TransactionType,
  amount: number,
  units: number | null | undefined,
): Holding {
  const s = db();
  const h = s.holdings.find((x) => x.id === holdingId);
  if (!h) throw new MockApiError("NOT_FOUND", "Holding not found");

  const unitsBefore = h.units;
  switch (type) {
    case "BUY":
    case "CONTRIBUTION":
      if (units) h.units += units;
      h.investedAmount += amount;
      if (h.valuationMode === "CASH_BALANCE") h.cashBalance += amount;
      break;
    case "SELL":
    case "WITHDRAWAL": {
      if (h.valuationMode === "CASH_BALANCE") {
        if (amount > h.cashBalance) throw new MockApiError("INSUFFICIENT_BALANCE", "Withdrawal exceeds cash balance");
        h.cashBalance -= amount;
      } else {
        const u = units ?? 0;
        if (u > unitsBefore) throw new MockApiError("INSUFFICIENT_BALANCE", "Withdrawal exceeds holding units");
        if (unitsBefore > 0) h.investedAmount -= h.investedAmount * (u / unitsBefore);
        h.units -= u;
      }
      break;
    }
    case "DIVIDEND":
    case "INTEREST":
      h.cashBalance += amount;
      break;
    case "VEST":
      if (units) h.vestedUnits = (h.vestedUnits ?? 0) + units;
      break;
    case "EXERCISE":
      if (units) {
        h.units += units;
        h.investedAmount += units * (h.strikePrice ?? 0);
      }
      break;
    case "FEE":
      h.investedAmount += amount;
      break;
    case "ADJUSTMENT":
      if (units != null) h.units = units;
      break;
  }
  if (h.units < 0 || h.cashBalance < 0) throw new MockApiError("NEGATIVE_BALANCE", "Balance cannot go below zero");
  h.updatedAt = new Date().toISOString();
  return h;
}

// ── account & holding CRUD (no UI existed for this before; PLAN.md always
// assumed accounts were seeded by SQL, but the contract has the routes) ────

type CreateAccountBody = z.input<typeof createAccountSchema>;
type UpdateAccountBody = z.input<typeof updateAccountSchema>;
type CreateHoldingBody = z.input<typeof createHoldingSchema>;
type UpdateHoldingBody = z.input<typeof updateHoldingSchema>;
type CreateLoanBody = z.input<typeof createLoanSchema>;
type CreateUserBody = z.input<typeof createUserSchema>;

export function addAccount(body: CreateAccountBody): Account {
  const s = db();
  const account: Account = {
    id: s.nextAccountId++,
    name: body.name,
    country: body.country,
    kind: body.kind,
    currency: body.currency,
    provider: body.provider ?? null,
    isActive: true,
    sortOrder: body.sortOrder ?? 0,
    createdAt: new Date().toISOString(),
  };
  s.accounts.push(account);
  return account;
}

export function updateAccount(id: number, patch: UpdateAccountBody): Account {
  const account = db().accounts.find((a) => a.id === id);
  if (!account) throw new MockApiError("NOT_FOUND", "Account not found");
  Object.assign(account, patch);
  return account;
}

export function deleteAccount(id: number): void {
  const s = db();
  const idx = s.accounts.findIndex((a) => a.id === id);
  if (idx === -1) throw new MockApiError("NOT_FOUND", "Account not found");
  s.accounts.splice(idx, 1);
  const holdingIds = s.holdings.filter((h) => h.accountId === id).map((h) => h.id);
  s.holdings = s.holdings.filter((h) => h.accountId !== id);
  s.transactions = s.transactions.filter((t) => !holdingIds.includes(t.holdingId));
}

export function addHolding(body: CreateHoldingBody): Holding {
  const s = db();
  const now = new Date().toISOString();
  const holding: Holding = {
    id: s.nextHoldingId++,
    accountId: body.accountId,
    name: body.name,
    valuationMode: body.valuationMode,
    symbolType: body.symbolType ?? null,
    symbol: body.symbol ?? null,
    currency: body.currency,
    units: body.units ?? 0,
    investedAmount: body.investedAmount ?? 0,
    cashBalance: body.cashBalance ?? 0,
    manualValue: body.manualValue ?? null,
    manualPrice: body.manualPrice ?? null,
    lastTransactionAt: null,
    strikePrice: body.strikePrice ?? null,
    vestedUnits: body.vestedUnits ?? null,
    notes: body.notes ?? null,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  };
  s.holdings.push(holding);
  return holding;
}

export function updateHolding(id: number, patch: UpdateHoldingBody): Holding {
  const holding = db().holdings.find((h) => h.id === id);
  if (!holding) throw new MockApiError("NOT_FOUND", "Holding not found");
  Object.assign(holding, patch, { updatedAt: new Date().toISOString() });
  return holding;
}

export function deleteHolding(id: number): void {
  const s = db();
  const idx = s.holdings.findIndex((h) => h.id === id);
  if (idx === -1) throw new MockApiError("NOT_FOUND", "Holding not found");
  s.holdings.splice(idx, 1);
  s.transactions = s.transactions.filter((t) => t.holdingId !== id);
}

export function addLoan(body: CreateLoanBody): Loan {
  const s = db();
  const now = new Date().toISOString();
  const loan: Loan = {
    id: s.nextLoanId++,
    name: body.name,
    lender: body.lender ?? null,
    currency: body.currency,
    principal: body.principal,
    outstandingPrincipal: body.outstandingPrincipal,
    interestRate: body.interestRate ?? null,
    emiAmount: body.emiAmount ?? null,
    tenureMonths: body.tenureMonths ?? null,
    startDate: body.startDate ?? null,
    nextDueDate: body.nextDueDate ?? null,
    status: "active",
    createdAt: now,
    updatedAt: now,
  };
  s.loans.push(loan);
  return loan;
}

export function addUser(body: CreateUserBody): User {
  const s = db();
  const email = body.email.trim().toLowerCase();
  if (s.users.some((u) => u.email === email)) {
    throw new MockApiError("EMAIL_TAKEN", "A user with that email already exists");
  }
  const user: User = {
    id: s.nextUserId++,
    email,
    name: body.name ?? null,
    // Mirrors the real POST /admin/users route: this endpoint can never
    // mint a second admin — that's a one-time hand-run SQL bootstrap.
    role: "member",
    status: "active",
    createdAt: new Date().toISOString(),
    lastLoginAt: null,
    emailVerifiedForDelivery: null,
  };
  s.users.push(user);
  return user;
}
